// src/App.js
// CHANGE: Add one import and one Route for the new /verify-reset-otp page.
//
// Full file shown so the diff context is unambiguous.
// Only additions are marked with ← NEW

import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider }  from "@/context/AuthContext";
import { CartProvider }  from "@/context/CartContext";
import { ToastProvider } from "@/shared";   // ← FIX: was missing, causes useToast crash in all admin pages

import RequireAuth  from "@/features/auth/RequireAuth";
import RequireAdmin from "@/features/auth/RequireAdmin";
import MainLayout   from "@/shared/components/MainLayout";

// ── Auth pages ────────────────────────────────────────────────────────────────
import Login           from "@/features/auth/Login";
import OTPVerify       from "@/features/auth/OTPVerify";
import ForgotPassword  from "@/features/auth/ForgotPassword";
import VerifyResetOTP  from "@/features/auth/VerifyResetOTP";    // ← NEW
import ResetPassword   from "@/features/auth/ResetPassword";

// ── Public pages (lazy) ───────────────────────────────────────────────────────
const Register         = lazy(() => import("@/features/auth/Register"));
const AccountDisabled  = lazy(() => import("@/features/auth/AccountDisabled"));
const Unauthorized     = lazy(() => import("@/features/auth/Unauthorized"));
const VerifyEmailRequired = lazy(() => import("@/features/auth/VerifyEmailRequired"));

const HomePage         = lazy(() => import("@/features/home/HomePage"));
const AboutPage        = lazy(() => import("@/features/about/AboutPage"));
const ContactPage      = lazy(() => import("@/features/contact/ContactPage"));
const ShopPage         = lazy(() => import("@/features/products/ShopPage"));
const CategoryPage     = lazy(() => import("@/features/products/CategoryPage"));
const ProductDetail    = lazy(() => import("@/features/products/ProductDetail"));
const CartPage         = lazy(() => import("@/features/cart/CartPage"));
const CustomJewelry    = lazy(() => import("@/features/custom/CustomJewelry"));
const SearchOverlay    = lazy(() => import("@/features/search/SearchOverlay"));

// ── Protected pages (lazy) ────────────────────────────────────────────────────
const CheckoutPage     = lazy(() => import("@/features/checkout/CheckoutPage"));
const OrderSuccess     = lazy(() => import("@/features/orders/OrderSuccess"));
const PaymentPending   = lazy(() => import("@/features/checkout/PaymentPending"));
const OrdersPage       = lazy(() => import("@/features/orders/OrdersPage"));
const OrderTrackingPage = lazy(() => import("@/features/orders/OrderTrackingPage"));
const AccountPage      = lazy(() => import("@/features/account/AccountPage"));

// ── Admin pages (lazy) ────────────────────────────────────────────────────────
const AdminRoutes      = lazy(() => import("@/features/admin/routes/AdminRoutes"));

function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <ToastProvider>
            <Toaster position="top-center" richColors closeButton />
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><span className="animate-pulse text-gray-400">Loading…</span></div>}>
            <Routes>

              {/* ── Public auth ──────────────────────────────────────────── */}
              <Route path="/login"              element={<Login />} />
              <Route path="/register"           element={<Register />} />
              <Route path="/verify-otp"         element={<OTPVerify />} />
              <Route path="/forgot-password"    element={<ForgotPassword />} />
              <Route path="/verify-reset-otp"   element={<VerifyResetOTP />} />  {/* ← NEW */}
              <Route path="/reset-password"     element={<ResetPassword />} />
              <Route path="/account-disabled"   element={<AccountDisabled />} />
              <Route path="/unauthorized"       element={<Unauthorized />} />
              <Route path="/verify-email-required" element={<VerifyEmailRequired />} />

              {/* ── Public + Protected pages (with Navbar / AnnouncementBar / BottomNav) */}
              <Route element={<MainLayout />}>
                <Route path="/"                   element={<HomePage />} />
                <Route path="/about"              element={<AboutPage />} />
                <Route path="/contact"            element={<ContactPage />} />
                <Route path="/shop"               element={<ShopPage />} />
                <Route path="/category/:slugOrId" element={<CategoryPage />} />
                <Route path="/product/:id"         element={<ProductDetail />} />
                <Route path="/products/:id"        element={<ProductDetail />} />
                <Route path="/cart"               element={<CartPage />} />
                <Route path="/custom-jewelry"     element={<CustomJewelry />} />
                <Route path="/search"             element={<SearchOverlay />} />

                {/* Protected pages nested inside MainLayout */}
                <Route element={<RequireAuth />}>
                  <Route path="/checkout"                   element={<CheckoutPage />} />
                  <Route path="/order-success/:orderId"     element={<OrderSuccess />} />
                  <Route path="/payment-pending/:orderId"   element={<PaymentPending />} />
                  <Route path="/orders"                     element={<OrdersPage />} />
                  <Route path="/orders/:orderId/track"      element={<OrderTrackingPage />} />
                  <Route path="/account"                    element={<AccountPage />} />
                </Route>
              </Route>

              {/* ── Admin pages ──────────────────────────────────────────── */}
              <Route element={<RequireAdmin />}>
                <Route path="/admin/*" element={<AdminRoutes />} />
              </Route>

              {/* ── Fallback ─────────────────────────────────────────────── */}
              <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>
          </Suspense>
          </ToastProvider>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;